/** Explicit partial-state coverage. The first feed needs account data and its own
 * authorized page, while legacy surfaces still require a full server snapshot.
 * Read results never overwrite a newer mutation, actor or navigation. */
export function createStartup(ctx) {
  let generation = 0;
  let pending;
  let postState;
  const coveredPosts = new Set();
  const partial = () => ctx.state?.projection === 'startup-v1';
  const routeKey = () => ctx.navigationKey?.() ?? ctx.route().join('/');
  const merge = (rows, key) => {
    for (const row of rows ?? []) {
      const index = ctx.state[key].findIndex((current) => current.id === row.id);
      if (index < 0) ctx.state[key].push(row);
      else ctx.state[key][index] = row;
    }
  };
  function dispose() {
    generation++;
    pending?.controller.abort();
    pending = undefined;
    coveredPosts.clear();
    postState = undefined;
  }
  async function ensureFullState() {
    if (!ctx.state) return false;
    if (!partial()) return true;
    const captured = ctx.state;
    const route = routeKey();
    if (pending?.state === captured && pending.route === route && pending.kind === 'full')
      return pending.task;
    pending?.controller.abort();
    const controller = new AbortController();
    const current = ++generation;
    const record = { state: captured, route, kind: 'full', controller };
    pending = record;
    record.task = (async () => {
      try {
        const full = await ctx.api('/api/state', undefined, { signal: controller.signal });
        if (current !== generation || controller.signal.aborted || routeKey() !== route)
          return false;
        if (ctx.state?.me.id !== captured.me.id) return false;
        if (ctx.state !== captured) return !partial();
        if (full.me.id !== captured.me.id) return false;
        ctx.state = full;
        return true;
      } finally {
        if (pending === record) pending = undefined;
      }
    })();
    return record.task;
  }
  async function loadPost(id) {
    const captured = ctx.state,
      route = routeKey();
    pending?.controller.abort();
    const controller = new AbortController(),
      current = ++generation;
    const record = { state: captured, route, kind: 'post', controller };
    pending = record;
    try {
      const result = await ctx.api(
        '/api/feed/hydrate',
        {
          ids: [id],
          format: 'wall',
          scope: 'all',
          mode: 'latest',
          contentScope: ctx.contentScope ?? 'ordinary',
        },
        { signal: controller.signal },
      );
      if (
        current !== generation ||
        controller.signal.aborted ||
        ctx.state !== captured ||
        routeKey() !== route
      )
        return false;
      merge(result.users, 'users');
      // Revocation is authoritative too: an omitted post must not survive a prior feed page.
      ctx.state.posts = ctx.state.posts.filter((post) => post.id !== id);
      merge(result.posts, 'posts');
      coveredPosts.add(id);
      return true;
    } finally {
      if (pending === record) pending = undefined;
    }
  }
  function guardRoute() {
    if (pending && pending.route !== routeKey()) {
      generation++;
      pending.controller.abort();
      pending = undefined;
    }
    // Games needs only boot account/settings/flags, never a full social hydration.
    if (!partial() || ['feed', 'learning', 'games'].includes(ctx.route()[0])) return false;
    if (postState !== ctx.state) {
      postState = ctx.state;
      coveredPosts.clear();
    }
    const [page, id] = ctx.route();
    const onePost =
      page === 'post' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id ?? '');
    if (onePost && coveredPosts.has(id)) return false;
    const route = routeKey(),
      captured = ctx.state;
    const loading = ctx.e('p', 'feed-loading route-loading', ctx.uiText('Loading…'));
    loading.setAttribute('role', 'status');
    loading.setAttribute('aria-busy', 'true');
    ctx.view.append(loading);
    const work = onePost ? loadPost(id) : ensureFullState();
    void work
      .then((accepted) => {
        if (accepted && ctx.state?.me.id === captured.me.id && routeKey() === route) ctx.render();
      })
      .catch((error) => {
        if (
          ctx.state !== captured ||
          routeKey() !== route ||
          !loading.isConnected ||
          error.name === 'AbortError'
        )
          return;
        loading.setAttribute('aria-busy', 'false');
        loading.textContent = error.message;
        ctx.view.append(ctx.button('Try again', () => ctx.render(), 'secondary-button'));
      });
    return true;
  }
  async function initialize({ renderCopy, accountDestination, referralHandoff, showLogin }) {
    const current = generation,
      captured = ctx.state;
    try {
      const snapshot = await ctx.api('/api/bootstrap');
      if (current !== generation || ctx.state !== captured) return;
      ctx.state = snapshot;
      try {
        await ctx.setLocale(snapshot.settings.locale);
      } catch (error) {
        if (current === generation && ctx.state === snapshot) ctx.toast(error.message);
      }
      if (current !== generation || ctx.state !== snapshot) return;
      ctx.experience =
        snapshot.settings.experience === 'circles'
          ? 'wall'
          : (snapshot.settings.experience ?? 'wall');
      renderCopy();
      if (
        snapshot.account?.verificationRequired ||
        snapshot.account?.onboardingStatus === 'pending' ||
        snapshot.account?.starterStatus === 'pending' ||
        referralHandoff.read()
      )
        ctx.go(accountDestination(location.hash, snapshot.account, ctx.referralDestination()));
      else ctx.render();
    } catch (error) {
      if (current !== generation || ctx.state !== captured) return;
      showLogin();
      if (error.message !== 'Sign in to continue.') ctx.toast(error.message);
    }
  }
  return { guardRoute, ensureFullState, dispose, initialize };
}
